import React from 'react';
import { CustomerData } from '@/pages/hettwer/types/OfferDetailsData';
import { getSensitiveFieldClassName, usePrivacyMode } from '@/utils/privacyMode';

interface CustomerBlockProps {
  customer: CustomerData;
  onChange: (customer: CustomerData) => void;
}

/**
 * CustomerBlock
 *
 * Editor block for customer information.
 * Follows the same layout, spacing and label conventions
 * as SummaryBlock, PositionsBlock and TotalsBlock.
 */
export const CustomerBlock: React.FC<CustomerBlockProps> = ({ customer, onChange }) => {
  const { isPrivacyMode } = usePrivacyMode();
  const update = (patch: Partial<CustomerData>) => onChange({ ...customer, ...patch });

  const renderInput = (
    label: string,
    value: string | null | undefined,
    onChangeValue: (value: string) => void,
    options?: { fullWidth?: boolean }
  ) => (
    <div className={`flex flex-col gap-1 ${options?.fullWidth ? 'md:col-span-2' : ''}`}>
      <label className="text-xs text-gray-600">{label}</label>
      <input
        className={`input input-sm w-full ${getSensitiveFieldClassName(isPrivacyMode)} ${
          isPrivacyMode ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
        value={value ?? ''}
        readOnly={isPrivacyMode}
        onChange={(e) => onChangeValue(e.target.value)}
      />
    </div>
  );

  const renderTextarea = (
    label: string,
    value: string | null | undefined,
    onChangeValue: (value: string) => void
  ) => (
    <div className="flex flex-col gap-1 md:col-span-2">
      <label className="text-xs text-gray-600">{label}</label>
      <textarea
        className={`textarea textarea-sm w-full ${getSensitiveFieldClassName(isPrivacyMode)} ${
          isPrivacyMode ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
        rows={3}
        value={value ?? ''}
        readOnly={isPrivacyMode}
        onChange={(e) => onChangeValue(e.target.value)}
      />
    </div>
  );

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Kunde</h3>
      </div>

      <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-5">
        {renderInput('Name / Firma', customer.name, (v) => update({ name: v }))}

        {renderInput('Ansprechpartner', customer.contact_person, (v) =>
          update({ contact_person: v })
        )}

        {renderInput('E-Mail', customer.email, (v) => update({ email: v }))}

        {renderInput('Telefon', customer.phone, (v) => update({ phone: v }))}

        {renderTextarea('Adresse', customer.address, (v) => update({ address: v }))}
      </div>
    </div>
  );
};
